import { PageTitle } from "@/components/common";
import { NFC_SERVICE_URL, NFC_WS_STATUS } from "@/constants";
import { cn } from "@/libs/cn";
import { useNFCStore } from "@/states/zustand/nfc";
import { useNotificationStore } from "@/states/zustand/notification";
import { Divider, Flex, Segmented } from "antd";
import { useEffect, useState } from "react";
import { NFCReader } from "./NFCReader";
import { NFCWriter } from "./NFCWriter";

enum NFC_TAB {
	READ = "READ",
	WRITE = "WRITE",
}

export function NFCContainer() {
	const [activeTab, setActiveTab] = useState<NFC_TAB>(NFC_TAB.READ);
	const { reader_status } = useNFCStore();
	const { setNotification } = useNotificationStore();

	useEffect(() => {
		let ws: WebSocket;
		let reconnectInterval: NodeJS.Timeout;

		const connect = () => {
			ws = new WebSocket(NFC_SERVICE_URL);
			const store = useNFCStore.getState();
			// checking whether the reader is connected or not.
			if (ws) store.setWS(ws);

			ws.onopen = () => {
				ws.send(
					JSON.stringify({ type: NFC_WS_STATUS.GET_READER_STATUS }),
				);
			};

			ws.onmessage = (msgEvent) => {
				const data = JSON.parse(msgEvent.data);

				console.log("nfc ws data: ", data);

				switch (data.type) {
					case NFC_WS_STATUS.READER_STATUS:
						setNotification({
							type: data?.success ? "success" : "error",
							message: data?.success
								? "Reader connected!"
								: "Reader disconnected!",
						});
						store.setReaderStatus(data.success);
						break;
					case NFC_WS_STATUS.CARD_STATUS:
						store.setCardStatus(data.success);
						break;
					case NFC_WS_STATUS.DATA_READ_SUCCESS: {
						// getting the deep_link from the card: which record_type is 'U'
						const deep_link: NdefRecord = data?.data?.find(
							(it: NdefRecord) =>
								it.record_type.toLowerCase() === "u",
						);

						// deep_link format => telegram://conversations/chat/10 : we only need Id <10> from the link.
						const parts = deep_link?.payload.split("/");
						const user_id = parts[parts.length - 1];

						if (!parseInt(user_id)) {
							setNotification({
								type: "warning",
								message: "No usable data found!",
							});
							return;
						}

						store.setReadData(user_id);
						break;
					}
					case NFC_WS_STATUS.DATA_READ_ERROR:
						setNotification({
							type: "warning",
							message: data.error,
						});
						break;
					case NFC_WS_STATUS.READER_ERROR:
						console.log(data?.error);
						break;
					case NFC_WS_STATUS.DATA_WRITE_SUCCESS:
						setNotification({
							type: "success",
							message: data.message,
						});
						break;
					case NFC_WS_STATUS.DATA_WRITE_ERROR:
						setNotification({
							type: "error",
							message: data.error,
						});
						break;
					default:
						break;
				}
			};

			ws.onclose = () => {
				console.log("Disconnected. Trying to reconnect...");
				reconnectInterval = setTimeout(connect, 2000);
			};
		};

		connect();

		return () => {
			if (ws) ws.close();
			if (reconnectInterval) clearInterval(reconnectInterval);
		};
	}, []);

	const onSegmentChange = (key: NFC_TAB) => {
		setActiveTab(key);
	};

	return (
		<>
			<PageTitle>NFC Station</PageTitle>
			<Divider />
			<Flex align="center" gap={8} className="!mb-6">
				<div
					className={cn(
						"w-4 h-4 aspect-square rounded-full bg-danger",
						{
							"bg-success": reader_status,
						},
					)}
				/>
				<p className="font-poppins text-base">
					NFC Reader {reader_status ? "Connected" : "Disconnected"}!
				</p>
			</Flex>

			<Segmented
				block
				options={[
					{
						value: NFC_TAB.READ,
						label: "Read NFC Tag",
					},
					{
						value: NFC_TAB.WRITE,
						label: "Write NFC Tag",
					},
				]}
				value={activeTab}
				onChange={onSegmentChange}
				size="large"
				rootClassName="nfc-segmented !mb-4"
			/>

			{activeTab === NFC_TAB.READ && <NFCReader />}
			{activeTab === NFC_TAB.WRITE && <NFCWriter />}
		</>
	);
}
